import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ClientData, ConfigurationExchangeInfo } from "@shared/types";
import { EXCHANGES, MAX_ACCOUNT_NAME_LENGTH } from "@shared/common/constants";
import { getAccountFullName } from "@shared/features/portfolio/utils";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { getAccountNameError } from "@shared/features/configuration/utils";
import { useClientsData } from "@shared/utils/hooks/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";

interface UseSelectSubExchangeProps {
  primaryExchange?: string;
  account?: ClientData;
  isEdit?: boolean;
}

const useSelectSubExchange = ({
  primaryExchange,
  account,
  isEdit,
}: UseSelectSubExchangeProps) => {
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { noFuturesAccountMessageData, quickConnectSucceeded } =
    useContext(QuickConnectContext) || {};
  const {
    aggExchanges,
    selectedSubExchanges,
    setSelectedSubExchanges,
    isDemoWorkspace,
    accountName,
    setAccountName,
    setAccountNameError,
    addClientSucceeded,
  } = useContext(AccountFormContext)!;
  const { clientsData: accountsList } = useClientsData() || {};

  const [accountNameInitialized, setAccountNameInitialized] = useState(false);

  const subExchanges = useMemo(
    () => (primaryExchange ? aggExchanges[primaryExchange] : []),
    [aggExchanges, primaryExchange]
  );

  useEffect(() => {
    if (!selectedSubExchanges && subExchanges?.length) {
      setSelectedSubExchanges(subExchanges);
    }
  }, [subExchanges, selectedSubExchanges]);

  const getAccountsNames = useCallback(
    (name: string) => {
      return selectedSubExchanges?.map((subExchange) =>
        getAccountFullName(name, subExchange).toLowerCase()
      );
    },
    [selectedSubExchanges]
  );

  const getDefaultNameByExchange = useCallback(() => {
    const selectedSubExchangesNames = selectedSubExchanges?.map(
      (subExchange) => subExchange.exchange
    );
    const existAccounts = accountsList?.filter((account) =>
      selectedSubExchangesNames?.includes(account.exchange)
    );
    let index;
    let defaultName = `My ${primaryExchange}`;
    if (isDemoWorkspace) defaultName = defaultName + " Demo";
    let defaultAccountsNames = getAccountsNames(defaultName);

    while (
      existAccounts?.some((account) =>
        defaultAccountsNames?.includes(account.client_name.toLowerCase())
      )
    ) {
      if (!index) {
        index = 2;
        defaultName = defaultName + ` ${index}`;
        defaultAccountsNames = getAccountsNames(defaultName);
      } else {
        index += 1;
        defaultName = defaultName.replace(/\d*$/, `${index}`);
        defaultAccountsNames = getAccountsNames(defaultName);
      }
    }
    return defaultName;
  }, [
    selectedSubExchanges,
    isDemoWorkspace,
    primaryExchange,
    accountsList,
    getAccountsNames,
  ]);

  useEffect(() => {
    if (accountName?.length && !accountNameInitialized) {
      setAccountNameInitialized(true);
    }
  }, [accountName, accountNameInitialized]);

  useEffect(() => {
    if (account) {
      setAccountName(account.client_name);
    }
  }, [account]);

  useEffect(() => {
    if (
      selectedSubExchanges &&
      accountName === undefined &&
      !accountNameInitialized &&
      !isEdit &&
      primaryExchange &&
      accountsList
    ) {
      const defaultName = getDefaultNameByExchange();
      setAccountName(defaultName);
    }
  }, [
    accountName,
    isEdit,
    primaryExchange,
    getDefaultNameByExchange,
    accountsList,
    isDemoWorkspace,
    accountNameInitialized,
    selectedSubExchanges,
  ]);

  const checkNameAlreadyUsed = useCallback(
    (name: string) => {
      const accountsNames = getAccountsNames(name);
      return accountsList
        ?.filter(
          (accountInfo) =>
            !account || account.client_id !== accountInfo.client_id
        )
        .some((accountInfo) =>
          isEdit
            ? accountInfo.client_name.toLowerCase() === name.toLowerCase()
            : accountsNames?.includes(accountInfo.client_name.toLowerCase())
        );
    },
    [accountsList, account, isEdit, getAccountsNames]
  );

  const isLongName = (name: string) => {
    return name.length > MAX_ACCOUNT_NAME_LENGTH;
  };

  const checkNameIsTooLong = useCallback(
    (name: string) => {
      if (isEdit) {
        return isLongName(name);
      }
      const accountsNames = getAccountsNames(name);
      return accountsNames?.some((accountName) => isLongName(accountName));
    },
    [isEdit, getAccountsNames]
  );

  const getIsChecked = (subExchange: ConfigurationExchangeInfo) =>
    !!selectedSubExchanges?.find(
      (selectedExchange) => selectedExchange.exchange === subExchange.exchange
    );

  const onCheckboxChange = (subExchange: ConfigurationExchangeInfo) => {
    const isChecked = getIsChecked(subExchange);
    let updatedExchanges: ConfigurationExchangeInfo[];
    if (isChecked) {
      updatedExchanges =
        selectedSubExchanges?.filter(
          (selectedExchange) =>
            selectedExchange.exchange !== subExchange.exchange
        ) || [];
    } else {
      updatedExchanges = [...(selectedSubExchanges || []), subExchange];
    }
    setSelectedSubExchanges(updatedExchanges);
  };

  const getExchangeError = (subExchange: ConfigurationExchangeInfo) => {
    return (
      (
        [EXCHANGES.BINANCE_FUTURES, EXCHANGES.BINANCE_COIN_FUTURES] as string[]
      ).includes(subExchange.exchange) && noFuturesAccountMessageData?.show
    );
  };

  const isNameAlreadyUsed = (name: string) => {
    return accountsList?.some(
      (accountInfo) =>
        accountInfo.client_name.toLowerCase() === name.toLowerCase()
    );
  };

  const getCheckboxData = (subExchange: ConfigurationExchangeInfo) => {
    const accountDisplayName =
      accountName && !isExternalConnectAccount
        ? getAccountFullName(accountName, subExchange)
        : undefined;
    const checked = getIsChecked(subExchange);
    return {
      key: subExchange.exchange,
      value: subExchange.exchange,
      checked,
      onChange: () => onCheckboxChange(subExchange),
      exchange: subExchange.exchange_display,
      exchangeError: checked && getExchangeError(subExchange),
      account: accountDisplayName,
      accountError:
        checked &&
        !addClientSucceeded &&
        !quickConnectSucceeded &&
        accountDisplayName &&
        (isLongName(accountDisplayName) ||
          isNameAlreadyUsed(accountDisplayName)),
      description: subExchange.description,
    };
  };

  const accountNameError = useMemo(() => {
    if (!accountNameInitialized || addClientSucceeded || quickConnectSucceeded)
      return;
    if (!accountName?.length) return "Please provide a name for the account!";
    else if (checkNameIsTooLong(accountName)) {
      return getAccountNameError();
    } else if (checkNameAlreadyUsed(accountName)) {
      return "This name is already used by another account.";
    }
  }, [
    accountName,
    checkNameIsTooLong,
    checkNameAlreadyUsed,
    accountNameInitialized,
    addClientSucceeded,
    quickConnectSucceeded,
  ]);

  useEffect(() => {
    setAccountNameError(!!accountNameError);
  }, [accountNameError]);

  const accountNameInputData = {
    label: "Account Name",
    value: accountName,
    onChange: (value: string) => setAccountName(value),
    error: accountNameError,
    shouldDisplay: !isExternalConnectAccount,
  };

  return {
    subExchanges,
    getCheckboxData,
    accountNameInputData,
    accountNameInitialized,
  };
};

export default useSelectSubExchange;
